import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import { 
  getInterventionsByTechnicien,
  getTypeInterventionById,
  getCentreById 
} from '../../data/mockData';
import { format, addDays, startOfWeek, addWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';

function AgendaTechnicien({ user }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('semaine'); // 'jour' ou 'semaine'

  const startDate = viewMode === 'semaine' 
    ? startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 }) 
    : currentDate;

  const getDaysToShow = () => {
    if (viewMode === 'jour') return [currentDate];
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(startDate, i));
    }
    return days;
  };

  const days = getDaysToShow();

  const navigatePrevious = () => {
    if (viewMode === 'jour') {
      setCurrentDate(addDays(currentDate, -1));
    } else {
      setCurrentDate(addWeeks(currentDate, -1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'jour') {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  const getInterventionsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return getInterventionsByTechnicien(user.id, dateStr);
  };

  const getTotalDuration = (interventions) => {
    return interventions.reduce((total, i) => total + (i.dureeEstimee || 0), 0);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Mon Agenda</h1>
          <p className="text-muted">Planning de mes interventions</p>
        </div>
      </div>

      <div className="agenda-controls">
        <div className="view-switcher">
          <button 
            className={`btn ${viewMode === 'jour' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('jour')}
          >
            Jour
          </button>
          <button 
            className={`btn ${viewMode === 'semaine' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('semaine')}
          >
            Semaine
          </button>
        </div>

        <div className="date-navigation">
          <button onClick={navigatePrevious} className="btn btn-secondary">
            <ChevronLeft size={20} />
          </button>
          <button onClick={navigateToday} className="btn btn-secondary">
            Aujourd'hui
          </button>
          <button onClick={navigateNext} className="btn btn-secondary">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="current-period">
          {viewMode === 'jour' ? (
            <h2>{format(currentDate, 'EEEE d MMMM yyyy', { locale: fr })}</h2>
          ) : (
            <h2>
              Semaine du {format(startDate, 'd MMM', { locale: fr })} au {format(addDays(startDate, 6), 'd MMM yyyy', { locale: fr })}
            </h2>
          )}
        </div>
      </div>

      <div className={`agenda-grid ${viewMode === 'jour' ? 'agenda-day' : 'agenda-week'}`}>
        {days.map((day) => {
          const interventions = getInterventionsForDay(day);
          const totalDuration = getTotalDuration(interventions);
          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

          return (
            <div key={day.toISOString()} className={`agenda-day-column ${isToday ? 'today' : ''}`}>
              <div className="day-header">
                <div>
                  <p className="day-name">{format(day, 'EEEE', { locale: fr })}</p>
                  <p className="day-number">{format(day, 'd MMM', { locale: fr })}</p>
                </div>
                {interventions.length > 0 && (
                  <div className="day-summary">
                    <span className="badge badge-primary">{interventions.length}</span>
                    <span className="text-muted text-sm">{totalDuration} min</span>
                  </div>
                )}
              </div>

              <div className="day-interventions">
                {interventions.length === 0 ? (
                  <div className="empty-day">
                    <Calendar size={32} className="icon-muted" />
                    <p>Aucune intervention</p>
                  </div>
                ) : (
                  interventions
                    .sort((a, b) => a.heurePrevu.localeCompare(b.heurePrevu))
                    .map(intervention => {
                      const type = getTypeInterventionById(intervention.typeInterventionId);
                      const centre = getCentreById(intervention.centreId);
                      
                      return (
                        <div 
                          key={intervention.id} 
                          className={`intervention-card ${intervention.statut}`}
                          style={{ borderLeftColor: type?.couleur }}
                        >
                          <div className="intervention-time">
                            <Clock size={16} />
                            {intervention.heurePrevu}
                          </div>
                          
                          <div className="intervention-info">
                            {type && (
                              <span 
                                className="type-badge type-badge-sm" 
                                style={{ backgroundColor: type.couleur }}
                              >
                                {type.code}
                              </span>
                            )}
                            <p className="intervention-plaque">{intervention.plaque}</p>
                            <p className="text-sm text-muted">{centre?.nom}</p>
                          </div>

                          <div className="intervention-duration">
                            <Clock size={14} className="icon-muted" />
                            <span className="text-sm">{intervention.dureeEstimee} min</span>
                          </div>

                          {intervention.commentaire && (
                            <div className="intervention-comment">
                              <p className="text-sm">{intervention.commentaire}</p>
                            </div>
                          )}

                          <div className="intervention-status">
                            <span className={`badge badge-sm ${
                              intervention.statut === 'terminee' ? 'badge-success' :
                              intervention.statut === 'en_cours' ? 'badge-warning' :
                              'badge-primary'
                            }`}>
                              {intervention.statut === 'terminee' ? 'Terminée' :
                               intervention.statut === 'en_cours' ? 'En cours' :
                               'Planifiée'}
                            </span>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AgendaTechnicien;

